import type { BulkApprovalWorkflowResult } from '@risksmart-app/trpc/src/helpers/run-bulk-approval-workflow';
import type { DeleteIssuesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteIssuesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useDeleteIssues = createMutationHook({
  name: 'deleteIssues',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.issue.delete.mutationOptions({ onSuccess }),
  mapInput: (variables: { Ids: string[] }) => ({ Ids: variables.Ids }),
  // RSP-5789 — the bulk gate partitions the batch; any ids that required
  // approval got a CR created server-side — surface those on the CR read
  // feeds immediately.
  refreshChangeRequestFeedsWhen: (result: BulkApprovalWorkflowResult) =>
    result.changeRequestIds.length > 0,
  invalidate: (trpc) => [
    trpc.frontend.issue.register.queryKey(),
    trpc.frontend.issue.issueById.queryKey(),
    trpc.frontend.issue.issuesByParentId.queryKey(),
    trpc.frontend.issue.issueAssessmentByParentId.queryKey(),
    // COI-889: the left-nav badge reads this count family.
    trpc.frontend.issue.issueCount.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  // BulkApprovalWorkflowResult partitions the batch: `applied` carries the rows
  // actually deleted; gate-required rows come back in `changeRequestIds` with no
  // error thrown. The legacy GraphQL shape only reports the deleted count.
  mapResponse: (result: BulkApprovalWorkflowResult): DeleteIssuesMutation => ({
    deleteIssuesById: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.applied.length,
    },
  }),
  graphqlDocument: DeleteIssuesDocument,
  graphqlVariables: (variables: { Ids: string[] }) => ({ Ids: variables.Ids }),
  evictFields: [
    'issue',
    'issue_aggregate',
    'issue_assessment_aggregate',
    'issue_assessment_audit',
  ],
});
